import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useLoginFlow } from '../hooks/useLoginFlow';
import { describeMfaError } from '../lib/mfaError';
import { MfaCodeForm } from './MfaCodeForm';

const loginSchema = z.object({
	email: z.string().min(1, 'Email is required').email('Enter a valid email'),
	password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginForm() {
	const {
		challenge,
		submitCredentials,
		submitMfa,
		isSubmittingCredentials,
		isSubmittingMfa,
		credentialsError,
		mfaError,
		resetChallenge,
	} = useLoginFlow();

	const form = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: { email: '', password: '' },
	});

	// The backend requires a second factor: render the challenge instead.
	if (challenge) {
		return (
			<div className="grid gap-4">
				<p className="text-muted-foreground text-sm">
					{challenge.methods.includes('totp')
						? 'Open your authenticator app and enter the 6-digit code.'
						: 'Enter one of your recovery codes.'}
				</p>
				<MfaCodeForm
					methods={challenge.methods}
					onSubmit={({ method, code }) =>
						submitMfa({
							challengeToken: challenge.challengeToken,
							method,
							code,
						})
					}
					isPending={isSubmittingMfa}
					error={mfaError}
					submitLabel="Verify"
					onCancel={resetChallenge}
					cancelLabel="Back to sign in"
					autoFocus
				/>
			</div>
		);
	}

	const onSubmit = (values: LoginFormValues) => {
		submitCredentials(values);
	};

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="grid gap-5"
				noValidate
			>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input
									type="email"
									placeholder="you@example.com"
									autoComplete="email"
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="password"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Password</FormLabel>
							<FormControl>
								<Input
									type="password"
									placeholder="••••••••"
									autoComplete="current-password"
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{credentialsError ? (
					<p role="alert" className="text-destructive text-sm">
						{describeMfaError(credentialsError).message}
					</p>
				) : null}

				<Button
					type="submit"
					size="lg"
					className="w-full"
					disabled={isSubmittingCredentials}
				>
					{isSubmittingCredentials ? (
						<Loader2 className="animate-spin" />
					) : null}
					Sign in
				</Button>
			</form>
		</Form>
	);
}
